import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CollapsibleSection } from '@builder';
import { EntryForm, InputType, SectionName } from '../CustomForm/CustomForm.types';
import { GENETIC_COUNSELING_TRANSLATION } from '@/modules/genetic-counseling/constants/common';
import { GroupIcon, StyledSectionTitle } from './ProfileDetails.styles';
import { Loader } from '@base-app';
import { getDateWithoutTZ, maskPhoneNumber } from '@/common/utils';
import { ProfileDetailsProps } from './ProfileDetails.type';

import EditCard from '../EditCard/EditCard';
import CustomForm from '../CustomForm/CustomForm';

const ProfileDetails: FC<ProfileDetailsProps> = ({ data, handleEditionMode, isFetching, isLoading, onSubmit }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);

  const [editPatientInfo, setEditPatientInfo] = useState<boolean>(false);

  const [patientDetails, setPatientDetails] = useState<EntryForm[]>();
  const [patientInfo, setPatientInfo] = useState<EntryForm[]>();
  const [patientService, setPatientService] = useState<EntryForm[]>();

  const handleEditPatientInfo = (isEditing: boolean): void => {
    setEditPatientInfo(isEditing);
  };

  const handleSubmit = (data: any): void => {
    handleEditPatientInfo(false);
    onSubmit?.(data);
  };

  const renderTitle = (): ReactNode => {
    return (
      <StyledSectionTitle>
        <GroupIcon />
        {t('patient_profile.section_profile')}
      </StyledSectionTitle>
    );
  };

  useEffect(() => {
    handleEditionMode(editPatientInfo);
  }, [editPatientInfo]);

  useEffect(() => {
    if (data) {
      setPatientDetails([
        {
          name: t('patient_profile.name'),
          required: true,
          section: SectionName.DETAILS,
          target: data.patientProfile.firstName,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.last_name'),
          required: true,
          section: SectionName.DETAILS,
          target: data.patientProfile.lastName,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.patient_id'),
          required: true,
          section: SectionName.DETAILS,
          target: data.patientProfile.patientID,
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.order_id'),
          required: true,
          section: SectionName.DETAILS,
          target: data.orderID,
          type: InputType.TEXT,
        },
        {
          name: t('patient_profile.date_of_birth'),
          required: true,
          section: SectionName.DETAILS,
          target: getDateWithoutTZ(data.patientProfile.dateOfBirth, 'MM/dd/yyyy'),
          type: InputType.TEXT,
          isSensitive: true,
        },
        {
          name: t('patient_profile.sex_at_birth'),
          required: true,
          section: SectionName.DETAILS,
          target: data.patientProfile.sexAtBirth,
          type: InputType.TEXT,
          isSensitive: true,
        },
      ]);

      setPatientInfo([
        {
          name: t('patient_profile.email'),
          required: true,
          section: SectionName.INFO,
          target: data.patientProfile.email,
          type: InputType.EMAIL,
          isSensitive: true,
        },
        {
          name: t('patient_profile.phone_number'),
          required: true,
          section: SectionName.INFO,
          target: maskPhoneNumber(data.patientProfile.phoneNumber),
          type: InputType.PHONE,
          isSensitive: true,
        },
      ]);

      setPatientService([
        {
          name: t('patient_profile.service'),
          options: [
            {
              id: data.serviceDetails.serviceID,
              label: data.serviceDetails.service,
            },
          ],
          required: true,
          section: SectionName.SERVICE,
          target: data.serviceDetails.serviceID,
          type: InputType.SELECT,
        },
        {
          name: t('patient_profile.specialty'),
          options: [
            {
              id: data.serviceDetails.specialtyID,
              label: data.serviceDetails.specialty,
            },
          ],
          required: true,
          section: SectionName.SERVICE,
          target: data.serviceDetails.specialtyID,
          type: InputType.SELECT,
        },
        {
          name: t('patient_profile.sub_specialty'),
          options: [
            {
              id: data.serviceDetails.subSpecialtyID,
              label: data.serviceDetails.subSpecialty,
            },
          ],
          required: true,
          section: SectionName.SERVICE,
          target: data.serviceDetails.subSpecialtyID,
          type: InputType.SELECT,
        },
      ]);
    }
  }, [data]);

  return (
    <CollapsibleSection title={renderTitle()} id="details">
      <EditCard css={{ marginBottom: '$6' }} title={t('patient_profile.patient_details')}>
        {(isLoading || isFetching) && <Loader css={{ height: '160px' }} />}
        {!isLoading && patientDetails && <CustomForm data={patientDetails} />}
      </EditCard>
      <EditCard
        css={{ marginBottom: '$6' }}
        isEditing={editPatientInfo}
        handleEdit={handleEditPatientInfo}
        title={t('patient_profile.contact_info')}
        isEditable={true}
      >
        {(isLoading || isFetching) && <Loader css={{ height: '80px' }} />}
        {!isLoading && patientInfo && (
          <CustomForm data={patientInfo} isEditing={editPatientInfo} onSubmit={handleSubmit} />
        )}
      </EditCard>
      <EditCard title={t('patient_profile.service_details')}>
        {(isLoading || isFetching) && <Loader css={{ height: '80px' }} />}
        {!isLoading && patientService && <CustomForm data={patientService} />}
      </EditCard>
    </CollapsibleSection>
  );
};

export default ProfileDetails;
